import {
  DESKTOP_BRIDGE_AUTH_TOKEN_KEY,
  DESKTOP_BRIDGE_HTTP_BASE,
  DESKTOP_BRIDGE_PROBE_TIMEOUT_MS,
  DESKTOP_BRIDGE_REQUEST_TIMEOUT_MS,
  DESKTOP_BRIDGE_SCAN_TIMEOUT_MS,
} from './config';
import type {
  DesktopBridgeReachabilityReason,
  DesktopBridgeVersion,
  DesktopPrinter,
  DesktopPrinterConfig,
  DesktopPrintJob,
} from './types';

export class DesktopBridgeError extends Error {
  constructor(
    /** i18n key (`bluetoothPrinter.desktop.errors.*`) or bridge message. */
    message: string,
    readonly status?: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'DesktopBridgeError';
  }
}

export function getStoredDesktopBridgeToken(): string {
  try {
    return globalThis.localStorage?.getItem(DESKTOP_BRIDGE_AUTH_TOKEN_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

function saveToken(token: string): void {
  try {
    if (token) globalThis.localStorage?.setItem(DESKTOP_BRIDGE_AUTH_TOKEN_KEY, token);
    else globalThis.localStorage?.removeItem(DESKTOP_BRIDGE_AUTH_TOKEN_KEY);
  } catch {
    /* quota / private mode */
  }
}

type RequestOptions = { method?: string; body?: unknown; auth?: boolean; timeoutMs?: number; token?: string };

async function requestOnce<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), options.timeoutMs ?? DESKTOP_BRIDGE_REQUEST_TIMEOUT_MS);
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = options.token ?? getStoredDesktopBridgeToken();
  if (options.auth !== false && token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  try {
    const response = await fetch(`${DESKTOP_BRIDGE_HTTP_BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
    const parsed = (await response.json().catch(() => null)) as
      | { ok: true; data: T }
      | { ok: false; error?: { code?: string; message?: string } }
      | null;
    if (!response.ok || !parsed || parsed.ok !== true) {
      const error = parsed && parsed.ok === false ? parsed.error : undefined;
      if (response.status === 403) {
        throw new DesktopBridgeError('bluetoothPrinter.desktop.errors.originDenied', 403, 'origin_denied');
      }
      throw new DesktopBridgeError(error?.message ?? `Print bridge HTTP ${response.status}`, response.status, error?.code);
    }
    return parsed.data;
  } catch (e) {
    if (e instanceof DesktopBridgeError) throw e;
    if (e instanceof Error && e.name === 'AbortError') {
      throw new DesktopBridgeError('bluetoothPrinter.desktop.errors.timeout', undefined, 'timeout');
    }
    throw new DesktopBridgeError('bluetoothPrinter.desktop.errors.notRunning', undefined, 'not_running');
  } finally {
    globalThis.clearTimeout(timer);
  }
}

/** Retries once with a fresh handshake token when the bridge was reinstalled (401). */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await requestOnce<T>(path, options);
  } catch (e) {
    if (e instanceof DesktopBridgeError && e.status === 401 && options.auth !== false) {
      const { token } = await handshakeDesktopBridge();
      return requestOnce<T>(path, { ...options, token });
    }
    throw e;
  }
}

export async function handshakeDesktopBridge(): Promise<{ token: string; version: string; apiVersion: number }> {
  const result = await requestOnce<{ token: string; version: string; apiVersion: number }>('/auth/handshake', {
    auth: false,
    timeoutMs: DESKTOP_BRIDGE_PROBE_TIMEOUT_MS,
  });
  saveToken(result.token);
  return result;
}

export type DesktopBridgeReachability =
  | { reachable: true; version: DesktopBridgeVersion }
  | { reachable: false; reason: DesktopBridgeReachabilityReason };

export async function probeDesktopBridge(): Promise<DesktopBridgeReachability> {
  try {
    const version = await requestOnce<DesktopBridgeVersion>('/version', {
      auth: false,
      timeoutMs: DESKTOP_BRIDGE_PROBE_TIMEOUT_MS,
    });
    if (!getStoredDesktopBridgeToken()) await handshakeDesktopBridge();
    return { reachable: true, version };
  } catch (e) {
    const code = e instanceof DesktopBridgeError ? e.code : undefined;
    return { reachable: false, reason: code === 'origin_denied' || code === 'timeout' ? code : 'not_running' };
  }
}

export type DesktopPrinterList = { printers: DesktopPrinter[]; defaultPrinterId: string | null };

/** `quick` re-reads the spooler and pings known LAN printers; `full` also sweeps the local /24 for port 9100. */
export function fetchDesktopPrinters(refresh?: 'quick' | 'full'): Promise<DesktopPrinterList> {
  const query = refresh ? `?refresh=${refresh}` : '';
  return request<DesktopPrinterList>(`/printers${query}`, {
    timeoutMs: refresh === 'full' ? DESKTOP_BRIDGE_SCAN_TIMEOUT_MS : DESKTOP_BRIDGE_REQUEST_TIMEOUT_MS * 2,
  });
}

export function addDesktopLanPrinter(host: string, port?: number, name?: string): Promise<DesktopPrinter> {
  return request<DesktopPrinter>('/printers/manual', { method: 'POST', body: { host, port, name } });
}

export function removeDesktopPrinter(printerId: string): Promise<{ removed: string }> {
  return request(`/printers/${encodeURIComponent(printerId)}`, { method: 'DELETE' });
}

export function setDesktopDefaultPrinter(printerId: string | null): Promise<{ defaultPrinterId: string | null }> {
  return request('/printers/default', { method: 'PUT', body: { printerId } });
}

export function saveDesktopPrinterConfigToBridge(
  printerId: string,
  config: Partial<DesktopPrinterConfig>,
): Promise<DesktopPrinterConfig> {
  return request(`/printers/${encodeURIComponent(printerId)}/config`, { method: 'PUT', body: config });
}

export function applyDesktopPrinterConfig(printerId: string): Promise<DesktopPrintJob> {
  return request(`/printers/${encodeURIComponent(printerId)}/apply`, { method: 'POST', body: {} });
}

export function printDesktopTestPage(printerId: string): Promise<DesktopPrintJob> {
  return request(`/printers/${encodeURIComponent(printerId)}/test`, { method: 'POST', body: {} });
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

export function submitDesktopPrintJob(body: {
  printerId?: string;
  bytes: Uint8Array;
  jobId?: string;
  raw?: boolean;
}): Promise<DesktopPrintJob> {
  return request('/print', {
    method: 'POST',
    body: { printerId: body.printerId, payloadBase64: bytesToBase64(body.bytes), jobId: body.jobId, raw: body.raw },
  });
}

export function fetchDesktopPrintJob(jobId: string): Promise<DesktopPrintJob> {
  return request(`/jobs/${encodeURIComponent(jobId)}`);
}
